import { Platform, Dimensions, type ViewStyle } from 'react-native'
import type { Theme } from '../src/theme/getTheme'

const SMALL_SCREEN_MAX_WIDTH = 380
const AVATAR_SIZE = 56
const TABLE_HEADER_ICON_SIZE = 18
const RANK_WIDTH = 32
const AVATAR_MARGIN_RIGHT = 12
const AVATAR_CELL_WIDTH = AVATAR_SIZE + AVATAR_MARGIN_RIGHT
const STAT_COLUMN_WIDTH = 44
const PTS_COLUMN_WIDTH = 54
const STATS_LEFT_MARGIN = 16
const ROW_PADDING_H = 12
const GOLD = '#F6E05E'
const SILVER = '#A0AEC0'
const BRONZE = '#D69E2E'

export const getGroupLeaderboardTableStyleSheet = (theme: Theme) => {
  const isSmall = Dimensions.get('window').width < SMALL_SCREEN_MAX_WIDTH
  const windowWidth = Dimensions.get('window').width
  return {
    list: { flex: 1, paddingBottom: 0 },
    listContent: { paddingTop: 8 },
    tableHeaderRank: {
      width: RANK_WIDTH,
    },
    tableHeaderAvatar: {
      width: AVATAR_CELL_WIDTH,
    },
    tableHeaderName: {
      flex: 1,
      minWidth: 0,
      writingDirection: 'ltr' as const,
    },
    tableHeaderNameIcon: {
      alignSelf: 'flex-start' as const,
    },
    tableHeader: {
      flexDirection: 'row' as const,
      writingDirection: 'ltr' as const,
      alignItems: 'center' as const,
      paddingVertical: 10,
      marginBottom: 0,
      paddingHorizontal: ROW_PADDING_H,
    },
    tableHeaderLabel: {
      fontSize: 12,
      fontWeight: '700' as const,
      color: theme.gray[600],
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
    },
    tableHeaderStatFirst: {
      width: STAT_COLUMN_WIDTH,
      marginLeft: STATS_LEFT_MARGIN,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      gap: 4,
    },
    tableHeaderStat: {
      width: STAT_COLUMN_WIDTH,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      gap: 4,
    },
    tableHeaderPts: {
      width: PTS_COLUMN_WIDTH,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      gap: 4,
    },
    tableHeaderCoinIcon: {
      width: TABLE_HEADER_ICON_SIZE,
      height: TABLE_HEADER_ICON_SIZE,
    },
    row: {
      flexDirection: 'row' as const,
      writingDirection: 'ltr' as const,
      alignItems: 'center' as const,
      paddingVertical: 12,
      paddingHorizontal: ROW_PADDING_H,
      marginBottom: 8,
      backgroundColor: theme.cardActiveBg,
      borderRadius: 16,
      shadowColor: theme.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 6,
      elevation: 3,
    },
    rowFirst: {
      borderWidth: 2,
      borderColor: GOLD,
      shadowColor: GOLD,
      shadowOpacity: 0.25,
      shadowRadius: 8,
    },
    rowSecond: {
      borderWidth: 2,
      borderColor: SILVER,
      shadowColor: SILVER,
      shadowOpacity: 0.2,
      shadowRadius: 6,
    },
    rowThird: {
      borderWidth: 2,
      borderColor: BRONZE,
      shadowColor: BRONZE,
      shadowOpacity: 0.2,
      shadowRadius: 6,
    },
    rowYou: {
      backgroundColor: theme.primaryLight + '18',
      borderWidth: 1,
      borderColor: theme.primary + '40',
    },
    rowPending: {
      backgroundColor: theme.gray[200],
      opacity: 0.6,
    },
    rank: { width: RANK_WIDTH, fontSize: 14, fontWeight: '700' as const, color: theme.gray[900] },
    rankFirst: { width: RANK_WIDTH, fontSize: 14, fontWeight: '800' as const, color: GOLD },
    rankSecond: { width: RANK_WIDTH, fontSize: 14, fontWeight: '800' as const, color: SILVER },
    rankThird: { width: RANK_WIDTH, fontSize: 14, fontWeight: '800' as const, color: BRONZE },
    avatar: {
      width: AVATAR_CELL_WIDTH,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    // The cell is wider than the avatar (it carries the margin), so the crest needs its own
    // avatar-sized anchor or it would hang off the cell instead of the avatar.
    avatarRankWrap: {
      position: 'relative' as const,
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    // Bottom-right of the avatar (specs/rank-on-social-screens AC-9, design 1b).
    rankCrest: {
      position: 'absolute' as const,
      right: -8,
      bottom: -6,
      zIndex: 2,
    },
    // In the name block's existing slack, above the YOU / Pending badges — the row does not grow.
    rankLevelPill: { marginTop: 2 },
    nameBlock: { flex: 1, writingDirection: 'ltr' as const, minWidth: 0 },
    name: {
      fontSize: 14,
      fontWeight: '600' as const,
      color: theme.gray[900],
      textAlign: 'left' as const,
    },
    youBadge: {
      marginTop: 2,
      paddingHorizontal: 8,
      paddingVertical: 2,
      alignSelf: 'flex-start' as const,
      backgroundColor: theme.primary + '25',
      borderRadius: 8,
    },
    youBadgeText: { fontSize: 11, fontWeight: '700' as const, color: theme.primary },
    pendingBadge: {
      marginTop: 4,
      paddingHorizontal: 8,
      paddingVertical: 2,
      alignSelf: 'flex-start' as const,
      backgroundColor: theme.gray[200],
      borderRadius: 8,
    },
    pendingBadgeText: { fontSize: 11, fontWeight: '600' as const, color: theme.gray[600] },
    statCellFirst: {
      width: STAT_COLUMN_WIDTH,
      marginLeft: STATS_LEFT_MARGIN,
      alignItems: 'center' as const,
    },
    statCell: {
      width: STAT_COLUMN_WIDTH,
      alignItems: 'center' as const,
    },
    statCellPts: {
      width: PTS_COLUMN_WIDTH,
      alignItems: 'center' as const,
    },
    stat: { fontSize: 14, fontWeight: '600' as const, color: theme.gray[900] },
    statPending: { fontSize: 14, fontWeight: '600' as const, color: theme.gray[600] },
    coinsDeltaPos: { fontSize: 15, fontWeight: '800' as const, color: theme.success },
    coinsDeltaNeg: { fontSize: 15, fontWeight: '800' as const, color: theme.error },
    points: { fontSize: 16, fontWeight: '700' as const, color: theme.primary },
    pointsPending: { fontSize: 16, fontWeight: '700' as const, color: theme.gray[600] },
    rowMenuTopRight: {
      position: 'absolute' as const,
      top: 2,
      right: 2,
      zIndex: 1,
    },
    rowMenuButton: {
      padding: 4,
      borderRadius: 6,
    },
    menuBackdrop: {
      flex: 1,
      backgroundColor: 'transparent',
      ...((Platform.OS === 'web' ? { cursor: 'default' } : {}) as ViewStyle),
    },
    rowMenuDropdown: {
      minWidth: isSmall ? 120 : 160,
      maxWidth: isSmall ? windowWidth - 32 : undefined,
      backgroundColor: theme.cardActiveBg,
      borderRadius: isSmall ? 10 : 12,
      shadowColor: theme.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 8,
      elevation: 4,
      overflow: 'hidden' as const,
      ...((Platform.OS === 'web' ? { cursor: 'default' } : {}) as ViewStyle),
    },
    rowMenuOption: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: isSmall ? 8 : 10,
      paddingVertical: isSmall ? 10 : 12,
      paddingHorizontal: isSmall ? 10 : 14,
      ...((Platform.OS === 'web' ? { cursor: 'pointer' } : {}) as ViewStyle),
    },
    rowMenuOptionDisabled: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: isSmall ? 8 : 10,
      paddingVertical: isSmall ? 10 : 12,
      paddingHorizontal: isSmall ? 10 : 14,
      opacity: 0.6,
    },
    rowMenuOptionTextBlock: {
      flex: 1,
    },
    rowMenuOptionText: {
      fontSize: isSmall ? 13 : 14,
      fontWeight: '500' as const,
      color: theme.gray[900],
    },
    rowMenuOptionTextDisabled: {
      fontSize: isSmall ? 13 : 14,
      fontWeight: '500' as const,
      color: theme.gray[500],
    },
    rowMenuOptionReason: {
      fontSize: isSmall ? 10 : 11,
      fontWeight: '400' as const,
      color: theme.gray[500],
      marginTop: 2,
    },
    rowMenuOptionTextDanger: {
      fontSize: isSmall ? 13 : 14,
      fontWeight: '500' as const,
      color: theme.error,
    },
  }
}
